import type { DataImportStartImportMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DataImportStartImportDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * CACHE ASYMMETRY (deliberate, documented exception to the web CLAUDE.md
 * cache-parity rule): the legacy Apollo path performs no cache eviction —
 * mirroring the previous inline `useMutation` usage — because the results
 * page tracks import status via an Apollo subscription that keeps those reads
 * live. The tRPC path instead invalidates the `dataImports` and
 * `dataImportById` query families in `onSuccess`, because TanStack Query has
 * no equivalent subscription keeping those reads fresh.
 *
 * NOTE: the tRPC path is ASYNC — it resolves as soon as the import pipeline
 * has been kicked off (status moves to 'initiatingImport' and the results
 * page's status subscription shows live progress), whereas the legacy Hasura
 * action ran synchronously. Consumers must not assume the import is complete
 * when the promise resolves.
 */
export const useDataImportStartImport = createMutationHook({
  name: 'dataImportStartImport',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.dataImport.startImport.mutationOptions({ onSuccess }),
  mapInput: (id: string) => ({ id }),
  invalidate: (trpc) => [
    trpc.frontend.dataImport.dataImports.queryKey(),
    trpc.frontend.dataImport.dataImportById.queryKey(),
  ],
  mapResponse: (result: {
    message: string;
  }): DataImportStartImportMutation => ({
    dataImportStartImport: {
      __typename: 'DataImportStartImportOutput',
      message: result.message,
    },
  }),
  graphqlDocument: DataImportStartImportDocument,
  graphqlVariables: (id: string) => ({ Id: id }),
});
